/**
 * Selleasy DOM corrections (E20-1483)
 *
 * Three things the stylesheet can't reach in the vendor's own markup: the money format,
 * footnote markers that stay keyboard-operable, and variant pickers for merchandising-only
 * options (see docs/dev/architecture/per-market-variant-skus.md).
 *
 * Every price the theme renders goes through Liquid's `money_without_trailing_zeros`, so the
 * site shows "$999", not "$999.00" (see snippets/bfx-promo-price.liquid). The Selleasy bundle
 * widget builds its own markup client-side from the app's API and formats its own money, so no
 * Liquid filter reaches it — the widget showed "$1,099.00" directly beneath a buy box showing
 * "$999", which is the sort of mismatch the shopper reads as two different prices.
 *
 * This can't be done in CSS: the amount is a single text node, and CSS cannot alter text.
 *
 * The widget re-renders on its own (ticking the add-on recalculates the total, and opening the
 * quick view builds a fresh <dialog>), so the correction has to re-apply rather than run once.
 * Rewriting a price replaces its text node, which wakes the observer again — the pass is
 * idempotent, so the second wake-up matches nothing and it settles immediately.
 *
 * Scoped to Selleasy's own containers. If the app is ever replaced (E20-1356) this file goes
 * with it, along with scss/components/_selleasy.scss.
 */
const initSelleasyFixes = () => {
  // The app block ships in the server-rendered HTML even before the app hydrates into it; the
  // quick view is a <dialog> appended to <body> the first time a card is opened.
  const WIDGET = '.lb-widget-ba';
  const PRICE = '.money, .lb-price-discount';

  // Footnote markers carried in from the product description, which anchor to #footnotes further
  // down the PDP.
  const FOOTNOTE = '.lb-product-info-description a[href^="#"]';

  // A trailing zero-cents on the amount, with any currency suffix left alone: "$1,099.00" →
  // "$1,099", "-$349.00" → "-$349", "$1,099.00 USD" → "$1,099 USD". Anything with real cents is
  // untouched, and a thousands separator can't match because digits follow it.
  // Both separators are accepted: fr-CA isn't live yet, but it is a supported locale and formats
  // the same amount as "1 099,00 $".
  const TRAILING_ZEROS = /(\d)[.,]00(?=\D*$)/;

  // The app's own picker element. It only suppresses itself for a "Default Title" product, so a
  // product whose one option is merchandising plumbing (the `Market` option) still gets a
  // dropdown, showing the shopper's own market code as its only value.
  const PICKER = 'lb-variant-picker';

  // Read once, synchronously: document.currentScript is null inside any later callback. Supplied
  // by layout/theme.liquid from snippets/utility.merchandising-option-names.liquid, so the cart
  // and this widget apply one list. Missing or empty, nothing is hidden.
  const MERCHANDISING_OPTION_NAMES = (
    document.currentScript?.dataset.merchandisingOptionNames || ''
  )
    .split(',')
    .map((name) => name.trim().toLowerCase())
    .filter(Boolean);

  // The option names are only exposed through the <select>'s label, which the app builds as
  // "<names> for <product title>", joining several names with its variant separator (" / " by
  // default). Hidden only when every name is listed, so a product mixing a merchandising option
  // with a real one keeps its picker. Anything that doesn't parse that way is left showing.
  const isMerchandisingOnly = (picker) => {
    const label = picker.querySelector('select')?.getAttribute('aria-label') || '';
    const end = label.indexOf(' for ');
    if (end < 1) return false;
    return label
      .slice(0, end)
      .split('/')
      .every((name) => MERCHANDISING_OPTION_NAMES.includes(name.trim().toLowerCase()));
  };

  // Hiding the host leaves the app's state alone: the variant it pre-selected (the only one the
  // storefront returns for this market) is still the one the bundle adds.
  const suppress = (picker) => {
    if (picker.hidden || !isMerchandisingOnly(picker)) return;
    picker.hidden = true;
  };

  const trim = (el) => {
    const text = el.textContent;
    if (!TRAILING_ZEROS.test(text)) return;
    const next = text.replace(TRAILING_ZEROS, '$1');
    // Guard the write as well as the test: an unchanged assignment would still replace the text
    // node and wake the observer for nothing.
    if (next !== text) el.textContent = next;
  };

  // scss/components/_selleasy.scss makes these markers look inert, but `pointer-events: none`
  // only blocks mouse and touch — they keep tabindex 0, so a keyboard user can still Tab to one
  // inside the dialog and press Enter, which follows the hash and scrolls the page behind it.
  // Dropping the href is what actually makes them inert: an <a> without one is neither focusable
  // nor a link, and the marker still reads as a marker.
  const neuter = (anchor) => {
    if (!anchor.hasAttribute('href')) return;
    anchor.removeAttribute('href');
  };

  const fixTree = (node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.matches(PRICE)) trim(node);
    node.querySelectorAll(PRICE).forEach(trim);
    if (node.matches(FOOTNOTE)) neuter(node);
    node.querySelectorAll(FOOTNOTE).forEach(neuter);
    // The picker element is attached before the app renders its <select> into it, so the select
    // arriving later is what wakes the observer — hence the climb back to the host.
    const host = node.closest(PICKER);
    if (host) suppress(host);
    node.querySelectorAll(PICKER).forEach(suppress);
  };

  // Re-checks only the mutated subtree rather than re-scanning the whole widget on every wake-up.
  const watch = (root) => {
    fixTree(root);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          const parent = mutation.target.parentElement;
          if (parent) fixTree(parent);
        } else {
          mutation.addedNodes.forEach(fixTree);
        }
      });
    });

    observer.observe(root, { childList: true, subtree: true, characterData: true });
  };

  document.querySelectorAll(WIDGET).forEach(watch);

  // The quick-view dialog is created on demand, so wait for it rather than assuming it exists.
  // Only direct children of <body> are inspected — the dialog is appended there — which keeps
  // this off the hot path for the rest of the page's own DOM churn.
  const bodyObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.tagName === 'DIALOG' && node.querySelector('.lb-widget, lb-upsell-dialog'))
          watch(node);
      });
    });
  });

  bodyObserver.observe(document.body, { childList: true });
};

initSelleasyFixes();
